import { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Plus, Filter, Search, ChevronDown, User, Calendar, Target, GitBranch, HelpCircle, X, Sparkles, Copy, Check, Lightbulb, BarChart3, Save, FileDown } from 'lucide-react';
import { projectRaidService } from '../../services/projectRaid';
import { aiInsightsService } from '../../services/aiInsights';
import { OwnerSelect } from '../common/OwnerSelect';
import { usersService, type User as AssignableUser } from '../../services/users';
import { loadAnalysis, saveAnalysis } from './analysisStore';
import { exportAnalysisPdf } from './exportAnalysisPdf';
import { toast } from 'sonner';

function parseRaidId(displayId: string): number | null {
  const m = /([0-9]+)/.exec(displayId);
  return m ? parseInt(m[1], 10) : null;
}

interface Issue {
  id: string;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  assignedTo: string;
  reportedBy: string;
  dateReported: string;
  targetResolution: string;
  actualResolution: string | null;
  impact: string;
  resolution: string;
}

interface IssuesSectionProps {
  projectId?: number;
  onChanged?: () => void;
}

function mapRaidToIssue(item: { id: number; title: string | null; description: string; status: string | null; owner: string | null; due_date: string | null; extra_metadata: Record<string, unknown> | null }): Issue {
  const meta = item.extra_metadata || {};
  return {
    id: `I-${String(item.id).padStart(3, '0')}`,
    title: item.title || item.description.slice(0, 50) || 'Issue',
    description: item.description,
    category: (meta.category as string) || 'Other',
    priority: (meta.priority as string) || 'Medium',
    status: item.status || 'Open',
    assignedTo: item.owner || '',
    reportedBy: (meta.reportedBy as string) || '',
    dateReported: (meta.dateReported as string) || '',
    targetResolution: item.due_date || '',
    actualResolution: (meta.actualResolution as string) || null,
    impact: (meta.impact as string) || 'Medium',
    resolution: (meta.resolution as string) || '',
  };
}

export function IssuesSection({ projectId, onChanged }: IssuesSectionProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [resolutionTool, setResolutionTool] = useState<'fishbone' | '5whys' | 'pareto' | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [showForm, setShowForm] = useState(false);
  // Real-user list backs the assignee filter dropdown — replaces the old
  // hardcoded Sarah Chen / Michael Torres / etc. options.
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  useEffect(() => {
    let cancelled = false;
    usersService
      .assignable({ limit: 200 })
      .then((list) => {
        if (!cancelled) setAssignableUsers(list);
      })
      .catch(() => {
        if (!cancelled) setAssignableUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadIssues = useCallback(() => {
    if (!projectId) {
      setIssues([]);
      return;
    }
    projectRaidService
      .list(projectId, 'issue')
      .then((list) => setIssues(list.map(mapRaidToIssue)))
      .catch(() => {
        toast.error('Failed to load issues');
        // Do NOT fall back to demo data on real projects — see RisksSection.
        setIssues([]);
      });
  }, [projectId]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const handleCreateIssue = async (data: {
    title: string;
    description: string;
    category: string;
    priority: string;
    assignedTo: string;
    targetResolution: string;
  }) => {
    if (!projectId) {
      toast.error('Save the project before logging issues');
      return;
    }
    try {
      await projectRaidService.create(projectId, {
        raid_type: 'issue',
        title: data.title,
        description: data.description,
        status: 'Open',
        owner: data.assignedTo || null,
        due_date: data.targetResolution || null,
        extra_metadata: {
          category: data.category,
          priority: data.priority,
          impact: data.priority,
          dateReported: new Date().toISOString().slice(0, 10),
        },
      });
      toast.success('Issue logged');
      setShowForm(false);
      loadIssues();
      onChanged?.();
    } catch {
      toast.error('Failed to log issue');
    }
  };

  const issueStats = {
    total: issues.length,
    open: issues.filter(i => i.status === 'Open').length,
    inProgress: issues.filter(i => i.status === 'In Progress').length,
    escalated: issues.filter(i => i.status === 'Escalated').length,
    resolved: issues.filter(i => i.status === 'Resolved').length,
    critical: issues.filter(i => i.priority === 'Critical').length,
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Open': return 'bg-blue-100 text-blue-700';
      case 'In Progress': return 'bg-purple-100 text-purple-700';
      case 'Escalated': return 'bg-red-100 text-red-700';
      case 'Resolved': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleOpenTool = (tool: 'fishbone' | '5whys' | 'pareto') => {
    if (!selectedIssue) {
      toast.error('Please select an issue first before using the resolution technique.');
      return;
    }
    setResolutionTool(tool);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl mb-1">Issue Log</h1>
          <p className="text-gray-600">Track and resolve project issues with AI-powered root cause analysis</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Log New Issue
        </button>
      </div>

      {showForm && (
        <IssueForm onClose={() => setShowForm(false)} onSave={handleCreateIssue} />
      )}

      {/* Issue Statistics */}
      <div className="grid grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-xs text-gray-600 mb-1">Total Issues</div>
          <div className="text-2xl">{issueStats.total}</div>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="text-xs text-blue-700 mb-1">Open</div>
          <div className="text-2xl text-blue-700">{issueStats.open}</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
          <div className="text-xs text-purple-700 mb-1">In Progress</div>
          <div className="text-2xl text-purple-700">{issueStats.inProgress}</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="text-xs text-red-700 mb-1">Escalated</div>
          <div className="text-2xl text-red-700">{issueStats.escalated}</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="text-xs text-red-700 mb-1">Critical</div>
          <div className="text-2xl text-red-700">{issueStats.critical}</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="text-xs text-green-700 mb-1">Resolved</div>
          <div className="text-2xl text-green-700">{issueStats.resolved}</div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search issues by title, ID, or assignee..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            Filters
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
        
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-4 gap-4">
            <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option>All Status</option>
              <option>Open</option>
              <option>In Progress</option>
              <option>Escalated</option>
              <option>Resolved</option>
            </select>
            <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option>All Priority</option>
              <option>Critical</option>
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select>
            <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option>All Categories</option>
              <option>Technical</option>
              <option>Resource</option>
              <option>Financial</option>
              <option>Security</option>
            </select>
            <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option>All Assignees</option>
              {assignableUsers.map((u) => {
                const label = (u.full_name && u.full_name.trim()) || u.email;
                return (
                  <option key={u.id} value={label}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Compact Issue List - Takes 2/3 of space */}
        <div className="col-span-2 space-y-3">
          <h2 className="text-lg flex items-center gap-2">
            Active Issues
            {selectedIssue && (
              <span className="text-sm text-gray-500">
                (Selected: {selectedIssue.id})
              </span>
            )}
          </h2>
          {issues.map((issue) => (
            <div 
              key={issue.id} 
              className={`bg-white rounded-lg border hover:shadow-md transition-all cursor-pointer ${
                selectedIssue?.id === issue.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
              }`}
              onClick={() => setSelectedIssue(issue)}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${
                      issue.priority === 'Critical' ? 'bg-red-100' :
                      issue.priority === 'High' ? 'bg-orange-100' :
                      'bg-yellow-100'
                    }`}>
                      <AlertCircle className={`w-4 h-4 ${
                        issue.priority === 'Critical' ? 'text-red-600' :
                        issue.priority === 'High' ? 'text-orange-600' :
                        'text-yellow-600'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-xs text-gray-600">{issue.id}</span>
                        <h3 className="font-medium text-sm truncate">{issue.title}</h3>
                      </div>
                      <p className="text-xs text-gray-600 mb-2 line-clamp-1">{issue.description}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs border ${getPriorityColor(issue.priority)}`}>
                          {issue.priority}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(issue.status)}`}>
                          {issue.status}
                        </span>
                        <span className="text-xs text-gray-600 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {issue.assignedTo.split(' ')[0]}
                        </span>
                        <span className="text-xs text-gray-600 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {issue.targetResolution}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Issue Resolution Techniques - Takes 1/3 of space */}
        <div className="space-y-3">
          <h2 className="text-lg">AI-Powered Resolution</h2>
          
          {/* Selection Prompt */}
          {!selectedIssue && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-amber-900 mb-1">
                    Select an Issue
                  </div>
                  <div className="text-xs text-amber-700">
                    Click on any issue from the list to enable AI-powered root cause analysis tools
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fishbone Diagram Tool */}
          <div className={`bg-white rounded-lg border p-4 transition-all ${
            selectedIssue ? 'border-blue-200 shadow-sm' : 'border-gray-200 opacity-60'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <GitBranch className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-sm">Fishbone Diagram</h3>
              <Sparkles className="w-4 h-4 text-purple-500 ml-auto" />
            </div>
            <p className="text-xs text-gray-600 mb-3">
              AI analyzes across 6 categories: People, Process, Equipment, Materials, Environment, Management
            </p>
            <button
              onClick={() => handleOpenTool('fishbone')}
              disabled={!selectedIssue}
              className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedIssue 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {selectedIssue ? 'Analyze with AI' : 'Select Issue First'}
            </button>
          </div>

          {/* 5 Whys Tool */}
          <div className={`bg-white rounded-lg border p-4 transition-all ${
            selectedIssue ? 'border-purple-200 shadow-sm' : 'border-gray-200 opacity-60'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle className="w-5 h-5 text-purple-600" />
              <h3 className="font-medium text-sm">5 Whys Analysis</h3>
              <Sparkles className="w-4 h-4 text-purple-500 ml-auto" />
            </div>
            <p className="text-xs text-gray-600 mb-3">
              AI guides iterative questioning to discover root cause with intelligent suggestions
            </p>
            <button
              onClick={() => handleOpenTool('5whys')}
              disabled={!selectedIssue}
              className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedIssue 
                  ? 'bg-purple-600 text-white hover:bg-purple-700' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {selectedIssue ? 'Analyze with AI' : 'Select Issue First'}
            </button>
          </div>

          {/* Pareto Analysis Tool */}
          <div className={`bg-white rounded-lg border p-4 transition-all ${
            selectedIssue ? 'border-green-200 shadow-sm' : 'border-gray-200 opacity-60'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              <h3 className="font-medium text-sm">Pareto Analysis</h3>
              <Sparkles className="w-4 h-4 text-purple-500 ml-auto" />
            </div>
            <p className="text-xs text-gray-600 mb-3">
              AI identifies vital few causes (80/20 rule) and prioritizes high-impact solutions
            </p>
            <button
              onClick={() => handleOpenTool('pareto')}
              disabled={!selectedIssue}
              className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedIssue 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {selectedIssue ? 'Analyze with AI' : 'Select Issue First'}
            </button>
          </div>

          {/* Selected Issue Info */}
          {selectedIssue && (
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-blue-600" />
                <div className="text-xs text-blue-700 font-medium">Selected for Analysis</div>
              </div>
              <div className="text-sm font-medium mb-1">{selectedIssue.id}</div>
              <div className="text-xs text-gray-700 mb-2">{selectedIssue.title}</div>
              <div className="pt-2 border-t border-blue-200">
                <div className="text-xs text-gray-600 mb-1">Category: {selectedIssue.category}</div>
                <div className="text-xs text-gray-600">Impact: {selectedIssue.impact}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fishbone Diagram Modal */}
      {resolutionTool === 'fishbone' && selectedIssue && (
        <FishboneDiagram
          issue={selectedIssue}
          projectId={projectId}
          onClose={() => setResolutionTool(null)}
        />
      )}

      {/* 5 Whys Modal */}
      {resolutionTool === '5whys' && selectedIssue && (
        <FiveWhysAnalysis
          issue={selectedIssue}
          projectId={projectId}
          onClose={() => setResolutionTool(null)}
        />
      )}

      {/* Pareto Analysis Modal */}
      {resolutionTool === 'pareto' && selectedIssue && (
        <ParetoAnalysis
          issue={selectedIssue}
          projectId={projectId}
          onClose={() => setResolutionTool(null)}
        />
      )}
    </div>
  );
}

// Issue create form
function IssueForm({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (data: {
    title: string;
    description: string;
    category: string;
    priority: string;
    assignedTo: string;
    targetResolution: string;
  }) => void;
}) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'Technical',
    priority: 'Medium',
    assignedTo: '',
    targetResolution: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl">Log New Issue</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm mb-2">Title *</label>
            <input
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              required
              placeholder="Short issue title"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm mb-2">Description *</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              required
              placeholder="Describe the issue"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-2">Category</label>
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option>Technical</option>
                <option>Resource</option>
                <option>Financial</option>
                <option>Security</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2">Priority *</label>
              <select
                name="priority"
                value={form.priority}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option>Critical</option>
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-2">Assigned To</label>
              <OwnerSelect
                name="assignedTo"
                value={form.assignedTo}
                onChange={(v) => setForm({ ...form, assignedTo: v })}
              />
            </div>
            <div>
              <label className="block text-sm mb-2">Target Resolution</label>
              <input
                type="date"
                name="targetResolution"
                value={form.targetResolution}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Log Issue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Fishbone Diagram Component with AI Assistance
function FishboneDiagram({ issue, projectId, onClose }: { issue: Issue; projectId?: number; onClose: () => void }) {
  const [causes, setCauses] = useState({
    people: ['', '', ''],
    process: ['', '', ''],
    equipment: ['', '', ''],
    materials: ['', '', ''],
    environment: ['', '', ''],
    management: ['', '', ''],
  });
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [copiedCategory, setCopiedCategory] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Sprint 22 — hydrate saved fishbone on open.
  useEffect(() => {
    const apiId = parseRaidId(issue.id);
    if (!projectId || apiId == null) return;
    loadAnalysis<{ state: typeof causes; saved_at: string }>(projectId, apiId, 'fishbone')
      .then((saved) => {
        if (saved?.state && typeof saved.state === 'object') {
          setCauses(saved.state);
        }
      })
      .catch((err) => console.warn('FishboneDiagram loadAnalysis failed:', err));
     
  }, [issue.id, projectId]);

  const handleSaveAnalysis = async () => {
    const apiId = parseRaidId(issue.id);
    if (!projectId || apiId == null) {
      toast.error('Save the project before persisting analysis');
      return;
    }
    setSaving(true);
    try {
      await saveAnalysis(projectId, apiId, 'fishbone', causes);
      toast.success('Fishbone saved');
    } catch {
      toast.error('Failed to save analysis');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      await exportAnalysisPdf({
        elementId: 'fishbone-pdf-root',
        filename: `fishbone-${issue.id}`,
        title: `Fishbone — ${issue.title}`,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  // Trigger AI-driven suggested actions via the existing analyzeIssue
  // endpoint, then map them into the 6 Fishbone categories (rotating
  // through the suggestions so each gets at least one seed).
  const handleGetAiRecommendations = async () => {
    const apiId = parseRaidId(issue.id);
    if (!projectId || apiId == null) {
      toast.error('Save the project before requesting AI recommendations');
      return;
    }
    setAiAnalyzing(true);
    try {
      const result = await aiInsightsService.analyzeIssue(apiId);
      const actions = result.suggested_actions || [];
      if (actions.length === 0) {
        toast('AI returned no specific suggestions for this issue');
      } else {
        const categoryKeys = ['people', 'process', 'equipment', 'materials', 'environment', 'management'] as const;
        setCauses((prev) => {
          const next = { ...prev };
          for (const cat of categoryKeys) {
            next[cat] = [...prev[cat]];
          }
          actions.forEach((suggestion, idx) => {
            const cat = categoryKeys[idx % categoryKeys.length];
            // Place into the first empty slot in that category; if full,
            // append (rare — categories have 3 slots, AI returns 3-6 ideas).
            const emptyIdx = next[cat].findIndex((c) => !c.trim());
            if (emptyIdx >= 0) {
              next[cat][emptyIdx] = suggestion;
            } else {
              next[cat].push(suggestion);
            }
          });
          return next;
        });
        setShowAISuggestions(true);
        toast.success('AI recommendations added');
      }
    } catch (err) {
      console.error(err);
      toast.error('AI analysis failed');
    } finally {
      setAiAnalyzing(false);
    }
  };

  const categories = [
    { key: 'people', label: 'People', color: 'bg-red-50 border-red-200', icon: '👥' },
    { key: 'process', label: 'Process', color: 'bg-blue-50 border-blue-200', icon: '⚙️' },
    { key: 'equipment', label: 'Equipment', color: 'bg-green-50 border-green-200', icon: '🔧' },
    { key: 'materials', label: 'Materials', color: 'bg-yellow-50 border-yellow-200', icon: '📦' },
    { key: 'environment', label: 'Environment', color: 'bg-purple-50 border-purple-200', icon: '🌍' },
    { key: 'management', label: 'Management', color: 'bg-orange-50 border-orange-200', icon: '📊' },
  ];

  const updateCause = (category: string, index: number, value: string) => {
    setCauses(prev => ({
      ...prev,
      [category]: prev[category as keyof typeof prev].map((c, i) => i === index ? value : c)
    }));
  };

  const getAISuggestions = (category: string) => {
    const suggestions: Record<string, string[]> = {
      people: [
        'Insufficient training on legacy systems',
        'Knowledge gap in performance optimization',
        'Resource turnover affecting expertise retention'
      ],
      process: [
        'Lack of regular performance testing procedures',
        'Inadequate code review process for database queries',
        'Missing SLA monitoring protocols'
      ],
      equipment: [
        'Database server hardware limitations',
        'Insufficient memory allocation',
        'Network bandwidth constraints'
      ],
      materials: [
        'Outdated API libraries and dependencies',
        'Legacy codebase with technical debt',
        'Inefficient query structures'
      ],
      environment: [
        'Production environment differs from test',
        'High concurrent user load not anticipated',
        'Third-party service dependencies'
      ],
      management: [
        'Performance benchmarks not clearly defined',
        'Budget constraints for infrastructure upgrades',
        'Competing priorities delaying optimization'
      ]
    };

    if (issue.category === 'Documentation') {
      return {
        people: ['Vendor technical writer unavailable', 'Communication gap with vendor', 'Language barriers in documentation'],
        process: ['Documentation review process unclear', 'No SLA for documentation delivery', 'Incomplete requirements gathering'],
        equipment: ['Documentation platform limitations', 'Version control system issues', 'Access restrictions to vendor systems'],
        materials: ['API specifications incomplete', 'Sample code examples missing', 'Integration guides outdated'],
        environment: ['Vendor organizational changes', 'Time zone coordination challenges', 'Remote collaboration difficulties'],
        management: ['Contract terms unclear on documentation', 'Escalation path not defined', 'Vendor relationship management gaps']
      };
    }

    if (issue.category === 'Financial') {
      return {
        people: ['Executive decision-maker availability', 'Budget approver priorities', 'Financial analyst workload'],
        process: ['Budget approval workflow complexity', 'Financial documentation requirements', 'Multi-level sign-off delays'],
        equipment: ['Financial system processing time', 'Approval workflow tool limitations', 'Document routing inefficiencies'],
        materials: ['Business case documentation gaps', 'ROI analysis incomplete', 'Cost-benefit justification unclear'],
        environment: ['Quarter-end financial freeze', 'Company-wide budget constraints', 'Economic uncertainty concerns'],
        management: ['Stakeholder alignment lacking', 'Strategic priority unclear', 'Risk appetite misalignment']
      };
    }

    return suggestions[category] || [];
  };

  const generateAIAnalysis = async () => {
    setAiAnalyzing(true);
    const numericId = parseRaidId(issue.id);
    try {
      if (numericId == null) throw new Error('Issue id missing');
      const result = await aiInsightsService.analyzeIssue(numericId);
      const actions = result.suggested_actions || [];
      const newCauses = { ...causes };
      // Distribute AI-suggested causes across the 6 categories. When fewer
      // than needed, supplement from the static category-specific bank.
      categories.forEach((cat, idx) => {
        const fallback = (() => {
          const raw = getAISuggestions(cat.key);
          return Array.isArray(raw) ? raw : (raw as Record<string, string[]>)[cat.key] || [];
        })();
        const slice = [actions[idx], actions[idx + categories.length], actions[idx + categories.length * 2]]
          .filter((s): s is string => Boolean(s));
        newCauses[cat.key as keyof typeof causes] = slice.length === 3 ? slice : fallback.slice(0, 3);
      });
      setCauses(newCauses);
      setShowAISuggestions(true);
      toast.success('AI analysis generated');
    } catch (err) {
      console.error(err);
      toast.error('AI analysis failed — showing reference suggestions');
      const newCauses = { ...causes };
      categories.forEach(cat => {
        const raw = getAISuggestions(cat.key);
        const arr = Array.isArray(raw) ? raw : (raw as Record<string, string[]>)[cat.key] || [];
        newCauses[cat.key as keyof typeof causes] = arr;
      });
      setCauses(newCauses);
      setShowAISuggestions(true);
    } finally {
      setAiAnalyzing(false);
    }
  };

  const copySuggestion = (category: string, index: number) => {
    const raw = getAISuggestions(category);
    const suggestions = Array.isArray(raw) ? raw : (raw as Record<string, string[]>)[category] || [];
    if (suggestions[index]) {
      updateCause(category, index, suggestions[index]);
      setCopiedCategory(`${category}-${index}`);
      setTimeout(() => setCopiedCategory(null), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl">Fishbone Diagram (Ishikawa)</h2>
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                AI-Powered
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Analyzing: <span className="font-mono">{issue.id}</span> - {issue.title}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6" id="fishbone-pdf-root">
          {/* AI Analysis Button */}
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <h3 className="font-medium">AI Root Cause Analysis</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Let AI analyze this {issue.category.toLowerCase()} issue and suggest potential causes across all categories
                </p>
              </div>
              <button
                onClick={generateAIAnalysis}
                disabled={aiAnalyzing}
                className="ml-4 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 font-medium flex items-center gap-2"
              >
                {aiAnalyzing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate AI Suggestions
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Problem Statement */}
          <div className="mb-6 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-orange-300 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-700 mb-2">Problem Statement (Effect)</div>
            <div className="text-lg font-medium mb-2">{issue.title}</div>
            <div className="text-sm text-gray-600">{issue.description}</div>
            <div className="flex items-center gap-3 mt-3">
              <span className="text-xs text-gray-600">Category: {issue.category}</span>
              <span className="text-xs text-gray-600">•</span>
              <span className="text-xs text-gray-600">Priority: {issue.priority}</span>
              <span className="text-xs text-gray-600">•</span>
              <span className="text-xs text-gray-600">Impact: {issue.impact}</span>
            </div>
          </div>

          {/* Categories Grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {categories.map((category) => (
              <div key={category.key} className={`rounded-lg border-2 p-4 ${category.color}`}>
                <h3 className="font-medium mb-3 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span className="text-lg">{category.icon}</span>
                    {category.label}
                  </span>
                  {showAISuggestions && (
                    <span className="text-xs text-gray-600 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      AI Suggested
                    </span>
                  )}
                </h3>
                <div className="space-y-2">
                  {causes[category.key as keyof typeof causes].map((cause, index) => (
                    <div key={index} className="relative">
                      <textarea
                        placeholder={`Potential cause ${index + 1}${showAISuggestions ? ' (AI suggested)' : ''}`}
                        value={cause}
                        onChange={(e) => updateCause(category.key, index, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        rows={2}
                      />
                      {showAISuggestions && !cause && (
                        <button
                          onClick={() => copySuggestion(category.key, index)}
                          className="absolute top-2 right-2 p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 text-xs flex items-center gap-1"
                        >
                          {copiedCategory === `${category.key}-${index}` ? (
                            <>
                              <Check className="w-3 h-3 text-green-600" />
                              <span className="text-green-600">Added</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3 h-3" />
                              Use AI
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Sprint 22 — restored as real actions:
                Save persists categories[] to extra_metadata.analyses.fishbone;
                Export PDF rasterizes the modal via jsPDF+html2canvas;
                Get AI Recommendations calls analyzeIssue and distributes
                suggested_actions across the 6 categories. */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSaveAnalysis}
              disabled={saving || !projectId}
              title={!projectId ? 'Save the project first' : undefined}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Analysis'}
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="w-4 h-4" />
              {exporting ? 'Exporting…' : 'Export as PDF'}
            </button>
            <button
              type="button"
              onClick={handleGetAiRecommendations}
              disabled={aiAnalyzing || !projectId}
              title={!projectId ? 'Save the project first' : undefined}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-4 h-4" />
              {aiAnalyzing ? 'Thinking…' : 'Get AI Recommendations'}
            </button>
            <button onClick={onClose} className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 5 Whys Analysis Component with AI Assistance
function FiveWhysAnalysis({ issue, projectId, onClose }: { issue: Issue; projectId?: number; onClose: () => void }) {
  const [whys, setWhys] = useState(['', '', '', '', '']);
  const [rootCause, setRootCause] = useState('');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [aiActioning, setAiActioning] = useState(false);

  // Sprint 22 — hydrate saved 5-Whys analysis on open.
  useEffect(() => {
    const apiId = parseRaidId(issue.id);
    if (!projectId || apiId == null) return;
    loadAnalysis<{ state: { whys: string[]; rootCause: string; correctiveAction: string }; saved_at: string }>(
      projectId, apiId, '5whys',
    )
      .then((saved) => {
        if (!saved?.state) return;
        if (Array.isArray(saved.state.whys)) setWhys(saved.state.whys);
        if (typeof saved.state.rootCause === 'string') setRootCause(saved.state.rootCause);
        if (typeof saved.state.correctiveAction === 'string') setCorrectiveAction(saved.state.correctiveAction);
      })
      .catch((err) => console.warn('FiveWhysAnalysis loadAnalysis failed:', err));
     
  }, [issue.id, projectId]);

  const handleSaveAnalysis = async () => {
    const apiId = parseRaidId(issue.id);
    if (!projectId || apiId == null) {
      toast.error('Save the project before persisting analysis');
      return;
    }
    setSaving(true);
    try {
      await saveAnalysis(projectId, apiId, '5whys', { whys, rootCause, correctiveAction });
      toast.success('5 Whys saved');
    } catch {
      toast.error('Failed to save analysis');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      await exportAnalysisPdf({
        elementId: '5whys-pdf-root',
        filename: `5whys-${issue.id}`,
        title: `5 Whys — ${issue.title}`,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  // "Create Action Items" — pre-fills the corrective-action textarea
  // from AI suggestions. Doesn't yet POST to the action-items endpoint
  // (that would need an extra confirmation step + assignee picker);
  // surfacing the AI's draft for the user to review and copy is the
  // honest minimum that respects "don't ship buttons that do nothing".
  const handleAiActions = async () => {
    const apiId = parseRaidId(issue.id);
    if (!projectId || apiId == null) {
      toast.error('Save the project before requesting AI actions');
      return;
    }
    setAiActioning(true);
    try {
      const result = await aiInsightsService.analyzeIssue(apiId);
      const lines = (result.suggested_actions || []).map((s, i) => `${i + 1}. ${s}`).join('\n');
      if (!lines) {
        toast('AI returned no specific actions for this issue');
      } else {
        setCorrectiveAction((prev) => {
          const sep = prev.trim() ? '\n\n' : '';
          return `${prev}${sep}AI-suggested actions:\n${lines}`;
        });
        toast.success('AI suggestions added to corrective action');
      }
    } catch (err) {
      console.error(err);
      toast.error('AI analysis failed');
    } finally {
      setAiActioning(false);
    }
  };
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [currentWhyIndex, setCurrentWhyIndex] = useState(0);
  const [showAISuggestions, setShowAISuggestions] = useState<boolean[]>([false, false, false, false, false]);

  const updateWhy = (index: number, value: string) => {
    const newWhys = [...whys];
    newWhys[index] = value;
    setWhys(newWhys);
    
    // Auto-advance to next why if current is filled
    if (value.trim() && index < 4) {
      setTimeout(() => setCurrentWhyIndex(index + 1), 300);
    }
  };

  const getAISuggestion = (whyIndex: number) => {
    const suggestions: Record<string, string[][]> = {
      'Technical': [
        ['Database queries are not optimized and using inefficient joins'],
        ['No performance testing was conducted during development phase', 'Performance requirements were not clearly defined in initial specs'],
        ['Development team lacked expertise in database optimization', 'Project timeline pressure led to shortcuts in code quality'],
        ['Training budget was cut due to cost constraints', 'Knowledge transfer from previous team was insufficient'],
        ['Executive leadership prioritized speed-to-market over technical excellence']
      ],
      'Documentation': [
        ['Vendor has not provided complete API documentation'],
        ['Documentation was not included in contract deliverables', 'Vendor documentation team is understaffed'],
        ['Contract negotiations rushed without detailed scope', 'Vendor made unrealistic commitments to win contract'],
        ['Procurement process did not involve technical reviewers', 'Vendor evaluation criteria lacked technical depth'],
        ['Organization lacks mature vendor management process']
      ],
      'Financial': [
        ['Budget approval has been pending for 2 weeks'],
        ['Executive approver is managing multiple priority initiatives', 'Financial controls require multiple sign-offs'],
        ['Organization implemented stricter budget controls this quarter', 'Previous projects had budget overruns'],
        ['Project portfolio lacks effective prioritization framework', 'Financial planning process is reactive not proactive'],
        ['PMO lacks authority to enforce portfolio management discipline']
      ]
    };

    const categorySuggestions = suggestions[issue.category] || suggestions['Technical'];
    return categorySuggestions[whyIndex] || [];
  };

  const generateAISuggestion = (index: number) => {
    const newShowAI = [...showAISuggestions];
    newShowAI[index] = true;
    setShowAISuggestions(newShowAI);
    
    // Simulate AI generation
    setTimeout(() => {
      const suggestions = getAISuggestion(index);
      setAiSuggestions(suggestions);
    }, 500);
  };

  const applySuggestion = (index: number, suggestion: string) => {
    updateWhy(index, suggestion);
    const newShowAI = [...showAISuggestions];
    newShowAI[index] = false;
    setShowAISuggestions(newShowAI);
  };

  const generateRootCauseAndAction = () => {
    const rootCauses: Record<string, string> = {
      'Technical': 'Organization lacks a culture of technical excellence and quality-first development, leading to accumulation of technical debt and performance issues.',
      'Documentation': 'Organization lacks mature vendor management processes with clear deliverable expectations and accountability mechanisms.',
      'Financial': 'PMO lacks authority and organizational maturity to enforce disciplined portfolio management and prioritization.'
    };

    const actions: Record<string, string> = {
      'Technical': '1) Implement mandatory performance testing gates in SDLC\n2) Establish technical excellence standards and training program\n3) Allocate 20% of sprint capacity for technical debt reduction\n4) Create peer review process for database queries',
      'Documentation': '1) Develop vendor management framework with clear deliverable SLAs\n2) Require technical review of all vendor contracts\n3) Implement vendor scorecard and quarterly business reviews\n4) Build internal API documentation standards',
      'Financial': '1) Establish PMO authority with executive sponsorship\n2) Implement portfolio prioritization framework (MoSCoW or scoring)\n3) Create fast-track approval process for high-priority items\n4) Conduct quarterly portfolio planning sessions with executives'
    };

    setRootCause(rootCauses[issue.category] || rootCauses['Technical']);
    setCorrectiveAction(actions[issue.category] || actions['Technical']);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl">5 Whys Analysis</h2>
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                AI-Guided
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Analyzing: <span className="font-mono">{issue.id}</span> - {issue.title}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6" id="5whys-pdf-root">
          {/* Problem Statement */}
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-700 mb-2">Problem Statement</div>
            <div className="text-lg font-medium mb-2">{issue.title}</div>
            <div className="text-sm text-gray-600">{issue.description}</div>
          </div>

          {/* 5 Whys Sequence */}
          <div className="space-y-4 mb-6">
            {whys.map((why, index) => (
              <div key={index} className={`transition-all ${currentWhyIndex === index ? 'ring-2 ring-purple-300 rounded-lg' : ''}`}>
                <div className="flex items-start gap-4 p-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-full flex items-center justify-center font-medium shadow-lg">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Why #{index + 1}: {index === 0 ? 'Why did this problem occur?' : `Why did "${whys[index - 1].substring(0, 30)}..." happen?`}
                      </label>
                      {!why && (
                        <button
                          onClick={() => generateAISuggestion(index)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-xs font-medium"
                        >
                          <Sparkles className="w-3 h-3" />
                          Get AI Help
                        </button>
                      )}
                    </div>
                    <textarea
                      value={why}
                      onChange={(e) => updateWhy(index, e.target.value)}
                      placeholder={`Enter the reason for ${index === 0 ? 'the problem' : `Why #${index}`}...`}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      rows={2}
                    />
                    
                    {/* AI Suggestions */}
                    {showAISuggestions[index] && aiSuggestions.length > 0 && (
                      <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                          <span className="text-xs font-medium text-purple-700">AI Suggestions</span>
                        </div>
                        <div className="space-y-2">
                          {aiSuggestions.map((suggestion, suggIdx) => (
                            <button
                              key={suggIdx}
                              onClick={() => applySuggestion(index, suggestion)}
                              className="w-full text-left px-3 py-2 bg-white border border-purple-200 rounded hover:bg-purple-50 text-sm transition-colors"
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* AI Generate Root Cause Button */}
          {whys.filter(w => w.trim()).length >= 3 && !rootCause && (
            <div className="mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Ready for AI Analysis</div>
                  <div className="text-xs text-gray-600">You've completed {whys.filter(w => w.trim()).length} whys. Generate root cause analysis.</div>
                </div>
                <button
                  onClick={generateRootCauseAndAction}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 font-medium flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Generate Analysis
                </button>
              </div>
            </div>
          )}

          {/* Root Cause */}
          <div className="mb-6 bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Root Cause Identified
            </label>
            <textarea
              value={rootCause}
              onChange={(e) => setRootCause(e.target.value)}
              placeholder="Based on the 5 Whys above, what is the true root cause?"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              rows={3}
            />
          </div>

          {/* Corrective Action */}
          <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Check className="w-4 h-4" />
              Corrective Action Plan
            </label>
            <textarea
              value={correctiveAction}
              onChange={(e) => setCorrectiveAction(e.target.value)}
              placeholder="What action should be taken to address the root cause?"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              rows={4}
            />
          </div>

          {/* Sprint 22 — Save persists 5-Whys to extra_metadata.analyses['5whys'];
              Export PDF rasterizes the modal; Create Action Items
              appends AI-suggested actions into the corrective-action
              textarea above (user reviews and copies). */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSaveAnalysis}
              disabled={saving || !projectId}
              title={!projectId ? 'Save the project first' : undefined}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Analysis'}
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="w-4 h-4" />
              {exporting ? 'Exporting…' : 'Export as PDF'}
            </button>
            <button
              type="button"
              onClick={handleAiActions}
              disabled={aiActioning || !projectId}
              title={!projectId ? 'Save the project first' : undefined}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-4 h-4" />
              {aiActioning ? 'Thinking…' : 'Create Action Items'}
            </button>
            <button onClick={onClose} className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Pareto Analysis Component with AI Assistance
function ParetoAnalysis({ issue, projectId, onClose }: { issue: Issue; projectId?: number; onClose: () => void }) {
  const [causes, setCauses] = useState([
    { id: 1, cause: '', frequency: 0, impact: 0 },
    { id: 2, cause: '', frequency: 0, impact: 0 },
    { id: 3, cause: '', frequency: 0, impact: 0 },
    { id: 4, cause: '', frequency: 0, impact: 0 },
    { id: 5, cause: '', frequency: 0, impact: 0 },
  ]);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [aiPlanning, setAiPlanning] = useState(false);
  const [aiPlan, setAiPlan] = useState<string | null>(null);

  // Sprint 22 — hydrate saved Pareto on open.
  useEffect(() => {
    const apiId = parseRaidId(issue.id);
    if (!projectId || apiId == null) return;
    loadAnalysis<{ state: typeof causes; saved_at: string }>(projectId, apiId, 'pareto')
      .then((saved) => {
        if (saved?.state && Array.isArray(saved.state)) setCauses(saved.state);
      })
      .catch((err) => console.warn('ParetoAnalysis loadAnalysis failed:', err));
     
  }, [issue.id, projectId]);

  const handleSaveAnalysis = async () => {
    const apiId = parseRaidId(issue.id);
    if (!projectId || apiId == null) {
      toast.error('Save the project before persisting analysis');
      return;
    }
    setSaving(true);
    try {
      await saveAnalysis(projectId, apiId, 'pareto', causes);
      toast.success('Pareto saved');
    } catch {
      toast.error('Failed to save analysis');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      await exportAnalysisPdf({
        elementId: 'pareto-pdf-root',
        filename: `pareto-${issue.id}`,
        title: `Pareto — ${issue.title}`,
      });
      toast.success('PDF downloaded');
    } catch (err) {
      console.error(err);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  // "Create Action Plan" — fetches AI-suggested actions and renders
  // them inline as a numbered plan beneath the Pareto. Doesn't yet
  // POST to project_actions (would need assignee picker + dates),
  // but surfacing the draft is the honest minimum.
  const handleAiPlan = async () => {
    const apiId = parseRaidId(issue.id);
    if (!projectId || apiId == null) {
      toast.error('Save the project before requesting an action plan');
      return;
    }
    setAiPlanning(true);
    try {
      const result = await aiInsightsService.analyzeIssue(apiId);
      const actions = result.suggested_actions || [];
      if (actions.length === 0) {
        toast('AI returned no specific actions');
        setAiPlan(null);
      } else {
        setAiPlan(actions.map((s, i) => `${i + 1}. ${s}`).join('\n'));
        toast.success('AI action plan generated');
      }
    } catch (err) {
      console.error(err);
      toast.error('AI analysis failed');
    } finally {
      setAiPlanning(false);
    }
  };

  const updateCause = (id: number, field: 'cause' | 'frequency' | 'impact', value: string | number) => {
    setCauses(prev => prev.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const generateAIAnalysis = async () => {
    setAnalyzing(true);
    const numericId = parseRaidId(issue.id);
    try {
      if (numericId == null) throw new Error('Issue id missing');
      const result = await aiInsightsService.analyzeIssue(numericId);
      const actions = result.suggested_actions || [];
      if (actions.length >= 3) {
        const probMap: Record<string, number> = { High: 80, Medium: 55, Low: 30 };
        const baseProb = probMap[result.probability] ?? 60;
        const baseImpact = probMap[result.impact] ?? 60;
        const newCauses = actions.slice(0, 5).map((a, idx) => ({
          id: idx + 1,
          cause: a,
          frequency: Math.max(20, baseProb - idx * 12),
          impact: Math.max(20, baseImpact - idx * 10),
        }));
        while (newCauses.length < 5) {
          newCauses.push({ id: newCauses.length + 1, cause: '', frequency: 0, impact: 0 });
        }
        setCauses(newCauses);
        setAiGenerated(true);
        setAnalyzing(false);
        toast.success('AI Pareto analysis generated');
        return;
      }
      throw new Error('Empty AI response');
    } catch (err) {
      console.error(err);
      toast.error('AI analysis failed — using reference data');
    }
    setTimeout(() => {
      const aiCauses: Record<string, Array<{ cause: string; frequency: number; impact: number }>> = {
        'Technical': [
          { cause: 'Inefficient database query joins and indexing', frequency: 85, impact: 90 },
          { cause: 'Legacy code architecture limitations', frequency: 65, impact: 70 },
          { cause: 'Insufficient server memory allocation', frequency: 45, impact: 60 },
          { cause: 'Third-party API response delays', frequency: 30, impact: 40 },
          { cause: 'Network latency during peak hours', frequency: 20, impact: 25 },
        ],
        'Documentation': [
          { cause: 'Vendor contract lacks documentation SLA', frequency: 80, impact: 85 },
          { cause: 'Missing technical reviewer in procurement', frequency: 70, impact: 75 },
          { cause: 'Vendor resource constraints', frequency: 50, impact: 55 },
          { cause: 'Time zone coordination challenges', frequency: 35, impact: 40 },
          { cause: 'Documentation platform access issues', frequency: 25, impact: 30 },
        ],
        'Financial': [
          { cause: 'Multi-level approval workflow complexity', frequency: 75, impact: 80 },
          { cause: 'Lack of portfolio prioritization framework', frequency: 70, impact: 75 },
          { cause: 'Executive availability and competing priorities', frequency: 60, impact: 65 },
          { cause: 'Incomplete business case documentation', frequency: 40, impact: 45 },
          { cause: 'Quarter-end budget freeze policies', frequency: 30, impact: 35 },
        ],
      };

      const newCauses = (aiCauses[issue.category] || aiCauses['Technical']).map((c, idx) => ({
        id: idx + 1,
        cause: c.cause,
        frequency: c.frequency,
        impact: c.impact,
      }));

      setCauses(newCauses);
      setAiGenerated(true);
      setAnalyzing(false);
    }, 1500);
  };

  const sortedCauses = [...causes]
    .filter(c => c.cause.trim())
    .map(c => ({
      ...c,
      score: (c.frequency * 0.5 + c.impact * 0.5),
    }))
    .sort((a, b) => b.score - a.score);

  const totalScore = sortedCauses.reduce((sum, c) => sum + c.score, 0);
  let cumulativePercent = 0;
  const paretoData = sortedCauses.map(c => {
    const percent = (c.score / totalScore) * 100;
    cumulativePercent += percent;
    return { ...c, percent, cumulativePercent };
  });

  const vitalFew = paretoData.filter(c => c.cumulativePercent <= 80);
  const trivialMany = paretoData.filter(c => c.cumulativePercent > 80);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl">Pareto Analysis (80/20 Rule)</h2>
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                AI-Powered
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Analyzing: <span className="font-mono">{issue.id}</span> - {issue.title}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6" id="pareto-pdf-root">
          {/* AI Analysis Button */}
          <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium">AI Pareto Analysis</h3>
                </div>
                <p className="text-sm text-gray-600">
                  AI identifies and prioritizes causes by frequency and impact to find the vital few (80/20 rule)
                </p>
              </div>
              <button
                onClick={generateAIAnalysis}
                disabled={analyzing}
                className="ml-4 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 font-medium flex items-center gap-2"
              >
                {analyzing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generate AI Analysis
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Problem Statement */}
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-700 mb-2">Problem Statement</div>
            <div className="text-lg font-medium mb-2">{issue.title}</div>
            <div className="text-sm text-gray-600">{issue.description}</div>
          </div>

          {/* Causes Input Table */}
          <div className="mb-6">
            <h3 className="font-medium mb-3">Potential Causes Analysis</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-12">#</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Potential Cause</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-32">Frequency %</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-32">Impact %</th>
                  </tr>
                </thead>
                <tbody>
                  {causes.map((cause, idx) => (
                    <tr key={cause.id} className="border-b border-gray-200 bg-white">
                      <td className="px-4 py-3 text-sm text-gray-600">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={cause.cause}
                          onChange={(e) => updateCause(cause.id, 'cause', e.target.value)}
                          placeholder="Enter potential cause..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={cause.frequency || ''}
                          onChange={(e) => updateCause(cause.id, 'frequency', parseInt(e.target.value) || 0)}
                          placeholder="0-100"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={cause.impact || ''}
                          onChange={(e) => updateCause(cause.id, 'impact', parseInt(e.target.value) || 0)}
                          placeholder="0-100"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pareto Chart Visualization */}
          {paretoData.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium mb-3">Pareto Chart - 80/20 Analysis</h3>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                {/* Visual Bar Chart */}
                <div className="space-y-3">
                  {paretoData.map((item, idx) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium truncate max-w-md">{item.cause}</span>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-gray-600">Score: {item.score.toFixed(1)}</span>
                          <span className="text-xs text-gray-600">Cumulative: {item.cumulativePercent.toFixed(1)}%</span>
                        </div>
                      </div>
                      <div className="relative h-8 bg-gray-100 rounded overflow-hidden">
                        <div 
                          className={`h-full flex items-center justify-end pr-2 text-white text-xs font-medium ${
                            item.cumulativePercent <= 80 ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-gray-400 to-gray-500'
                          }`}
                          style={{ width: `${item.percent}%` }}
                        >
                          {item.percent.toFixed(1)}%
                        </div>
                        {item.cumulativePercent <= 80 && item.cumulativePercent > 60 && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-white">
                            80% Line
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 80/20 Insights */}
          {paretoData.length > 0 && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Vital Few - Focus Here */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-5 h-5 text-green-600" />
                  <h3 className="font-medium text-green-900">Vital Few (Focus Here)</h3>
                </div>
                <p className="text-xs text-green-700 mb-3">
                  These {vitalFew.length} cause(s) contribute to ~80% of the problem. Prioritize addressing these for maximum impact.
                </p>
                <div className="space-y-2">
                  {vitalFew.map((item, idx) => (
                    <div key={idx} className="bg-white border border-green-200 rounded p-3">
                      <div className="text-sm font-medium text-gray-900 mb-1">{idx + 1}. {item.cause}</div>
                      <div className="flex gap-4 text-xs text-gray-600">
                        <span>Frequency: {item.frequency}%</span>
                        <span>Impact: {item.impact}%</span>
                        <span className="font-medium text-green-700">Score: {item.score.toFixed(1)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trivial Many - Lower Priority */}
              <div className="bg-gradient-to-br from-gray-50 to-slate-50 border-2 border-gray-300 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="w-5 h-5 text-gray-600" />
                  <h3 className="font-medium text-gray-900">Trivial Many (Monitor)</h3>
                </div>
                <p className="text-xs text-gray-700 mb-3">
                  These {trivialMany.length} cause(s) contribute to ~20% of the problem. Address after vital few are resolved.
                </p>
                <div className="space-y-2">
                  {trivialMany.map((item, idx) => (
                    <div key={idx} className="bg-white border border-gray-200 rounded p-3">
                      <div className="text-sm font-medium text-gray-900 mb-1">{vitalFew.length + idx + 1}. {item.cause}</div>
                      <div className="flex gap-4 text-xs text-gray-600">
                        <span>Frequency: {item.frequency}%</span>
                        <span>Impact: {item.impact}%</span>
                        <span className="font-medium">Score: {item.score.toFixed(1)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Recommended Actions */}
          {aiGenerated && vitalFew.length > 0 && (
            <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-blue-900">AI Recommended Actions</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-700">
                <p><strong>Priority 1:</strong> Immediately address "{vitalFew[0]?.cause}" as it has the highest impact</p>
                {vitalFew[1] && <p><strong>Priority 2:</strong> Follow up with "{vitalFew[1]?.cause}" for sustained improvement</p>}
                <p><strong>Quick Win:</strong> Allocate 80% of resources to the vital few causes for maximum ROI</p>
                <p><strong>Monitoring:</strong> Track the trivial many causes but avoid over-investing resources</p>
              </div>
            </div>
          )}

          {/* Sprint 22 — Save persists ranked causes to extra_metadata.analyses.pareto;
              Export PDF rasterizes the chart; Create Action Plan
              calls analyzeIssue and renders the suggestions inline
              as a numbered plan below. */}
          {aiPlan && (
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                  <h4 className="font-medium text-blue-900">AI-Suggested Action Plan</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setAiPlan(null)}
                  className="text-blue-700 hover:text-blue-900"
                  aria-label="Dismiss action plan"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{aiPlan}</pre>
            </div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSaveAnalysis}
              disabled={saving || !projectId}
              title={!projectId ? 'Save the project first' : undefined}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Analysis'}
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exporting}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="w-4 h-4" />
              {exporting ? 'Exporting…' : 'Export as PDF'}
            </button>
            <button
              type="button"
              onClick={handleAiPlan}
              disabled={aiPlanning || !projectId}
              title={!projectId ? 'Save the project first' : undefined}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-4 h-4" />
              {aiPlanning ? 'Thinking…' : 'Create Action Plan'}
            </button>
            <button onClick={onClose} className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
